import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { User, Lock, Eye, EyeOff, Fingerprint, Languages } from 'lucide-react';
import { setAuth } from '@/lib/auth';
import api from '@/lib/api';

function b64urlToArr(str) {
  str += '='.repeat((4 - str.length % 4) % 4);
  const bin = atob(str.replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(bin, c => c.charCodeAt(0));
}
function arrToB64url(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [biometricLoading, setBiometricLoading] = useState(false);
  const [hasBiometric, setHasBiometric] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const toggleLang = () => {
    const next = i18n.language === 'en' ? 'hi' : 'en';
    i18n.changeLanguage(next);
    localStorage.setItem('prismoline_lang', next);
  };

  useEffect(() => {
    setHasBiometric('credentials' in navigator && 'PublicKeyCredential' in window);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) { toast.error('Please fill all fields'); return; }
    setLoading(true);
    try {
      const { data } = await api.post('/api/auth/login', { username, password });
      setAuth(data.token, { ...data.user, must_change_password: data.must_change_password });
      toast.success('Welcome back!');
      if (data.must_change_password) {
        navigate('/change-password');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Login failed');
    } finally { setLoading(false); }
  };

  const handleBiometricLogin = async () => {
    if (!username) { toast.error('Enter your username first'); return; }
    setBiometricLoading(true);
    try {
      const { data: options } = await api.post('/api/webauthn/login/options', { email: username });
      const publicKey = {
        challenge: b64urlToArr(options.challenge),
        rpId: options.rpId,
        allowCredentials: options.allowCredentials.map(c => ({
          id: b64urlToArr(c.id),
          type: c.type,
        })),
        userVerification: options.userVerification,
        timeout: options.timeout,
      };
      const assertion = await navigator.credentials.get({ publicKey });
      const { data } = await api.post('/api/webauthn/login/verify', {
        id: assertion.id,
        rawId: arrToB64url(assertion.rawId),
        response: {
          clientDataJSON: arrToB64url(assertion.response.clientDataJSON),
          authenticatorData: arrToB64url(assertion.response.authenticatorData),
          signature: arrToB64url(assertion.response.signature),
        },
        type: assertion.type,
      });
      setAuth(data.token, data.user);
      toast.success('Welcome back!');
      navigate('/dashboard');
    } catch (err) {
      if (err.name === 'NotAllowedError') toast.error('Biometric cancelled');
      else if (err.response?.status === 404) toast.error('No biometric registered for this email. Set up in Profile > Security.');
      else toast.error(err.response?.data?.detail || 'Biometric login failed');
    }
    setBiometricLoading(false);
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left - Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-[#F7FAFF] relative">
        <button onClick={toggleLang} className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="login-lang-toggle">
          <Languages className="w-3.5 h-3.5" /> {i18n.language === 'en' ? 'हिन्दी' : 'English'}
        </button>
        <div className="w-full max-w-md">
          <div className="mb-8">
            <img src="/prismoline-logo.png" alt="Prismoline" className="h-10" data-testid="login-logo" />
          </div>

          <Card className="border-slate-200 shadow-lg">
            <CardHeader className="pb-4">
              <h2 className="text-xl font-semibold text-[#0B1B33]">{t('login.title')}</h2>
              <p className="text-sm text-slate-500">{t('login.subtitle')}</p>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="username" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('login.username')}</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="username" type="text" placeholder="PRI0001" value={username} onChange={e => setUsername(e.target.value.toUpperCase())} className="pl-10 h-11" data-testid="login-username-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{t('login.password')}</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input id="password" type={showPw ? 'text' : 'password'} placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} className="pl-10 pr-10 h-11" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button type="submit" disabled={loading} className="w-full h-11 bg-[#0D2850] hover:bg-[#0B2246] text-white font-semibold" data-testid="login-submit-button">
                  {loading ? t('login.signing') : t('login.signIn')}
                </Button>
              </form>

              {hasBiometric && (
                <div className="mt-4">
                  <div className="relative my-3"><div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200"></div></div><div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400">or</span></div></div>
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full h-11 gap-2 border-slate-300 text-[#0D2850] hover:bg-slate-50 font-medium"
                    onClick={handleBiometricLogin}
                    disabled={biometricLoading}
                    data-testid="biometric-login-btn"
                  >
                    <Fingerprint className="w-5 h-5" />
                    {biometricLoading ? 'Authenticating...' : t('login.biometricLogin')}
                  </Button>
                </div>
              )}

              <p className="text-xs text-slate-400 text-center mt-4">{t('login.forgotPassword')}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right - Brand Panel */}
      <div className="hidden lg:flex flex-1 bg-[#0D2850] items-center justify-center relative overflow-hidden" style={{background: 'linear-gradient(135deg, #0D2850 0%, #0B2246 60%, #091C3A 100%)'}}>
        <div className="absolute inset-0" style={{backgroundImage: 'repeating-linear-gradient(135deg, rgba(245,203,8,0.05) 0 8px, transparent 8px 28px)'}} />
        <div className="relative z-10 text-center px-12">
          <img src="/prismoline-logo.png" alt="Prismoline" className="h-16 brightness-0 invert mx-auto mb-8" />
          <p className="text-white/60 text-sm max-w-sm mx-auto leading-relaxed">Comprehensive HR Management System for streamlined workforce management</p>
        </div>
      </div>
    </div>
  );
}
